import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { ModalAddressResult } from '@/components/organisms/AddressSearchModal';
import { Prefecture, Town, useAddressSearch } from '@/hooks/useAddressSearch';

type AddressSearchPayload = {
  postal_code1?: string;
  postal_code2?: string;
  address?: string;
};

interface AddressFieldNames<TFieldValues extends FieldValues> {
  postalCode?: Path<TFieldValues>;
  postalCode1?: Path<TFieldValues>;
  postalCode2?: Path<TFieldValues>;
  address: Path<TFieldValues>;
  prefectureId?: Path<TFieldValues>;
}

interface UseAddressSearchModalHelperParams<TFieldValues extends FieldValues> {
  postalCodeValue?: string;
  addressValue?: string;
  isReadOnlyMode?: boolean;
  prefectures?: Prefecture[];
  setValue: UseFormSetValue<TFieldValues>;
  fieldNames: AddressFieldNames<TFieldValues>;
  addressSearchMutation: ReturnType<typeof useAddressSearch>;
}

const getAddressFromTown = (result: Town): string =>
  result.full_address || `${result.prefecture_name}${result.city_name}${result.name || ''}`;

const normalizeFullWidthPostalCode = (value: string): string =>
  value.replace(/[０-９－]/g, char =>
    char === '－' ? '-' : String.fromCharCode(char.charCodeAt(0) - 0xfee0)
  );

export const useAddressSearchModalHelper = <TFieldValues extends FieldValues>({
  postalCodeValue,
  addressValue,
  isReadOnlyMode = false,
  prefectures,
  setValue,
  fieldNames,
  addressSearchMutation,
}: UseAddressSearchModalHelperParams<TFieldValues>) => {
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const setFieldValue = useCallback(
    <TFieldName extends Path<TFieldValues>>(
      fieldName: TFieldName,
      value: PathValue<TFieldValues, TFieldName>
    ) => {
      setValue(fieldName, value, { shouldValidate: true });
    },
    [setValue]
  );

  const setPostalCodeValue = useCallback(
    (postalCode: string) => {
      if (fieldNames.postalCode) {
        setFieldValue(
          fieldNames.postalCode,
          postalCode as PathValue<TFieldValues, typeof fieldNames.postalCode>
        );
        return;
      }
      if (fieldNames.postalCode1) {
        setFieldValue(
          fieldNames.postalCode1,
          postalCode.substring(0, 3) as PathValue<TFieldValues, typeof fieldNames.postalCode1>
        );
      }
      if (fieldNames.postalCode2) {
        setFieldValue(
          fieldNames.postalCode2,
          postalCode.substring(3, 7) as PathValue<TFieldValues, typeof fieldNames.postalCode2>
        );
      }
    },
    [fieldNames.postalCode, fieldNames.postalCode1, fieldNames.postalCode2, setFieldValue]
  );

  const applyTownResult = useCallback(
    (result: Town) => {
      setFieldValue(
        fieldNames.address,
        getAddressFromTown(result) as PathValue<TFieldValues, typeof fieldNames.address>
      );
      setPostalCodeValue(result.postal_code1 + result.postal_code2);

      if (fieldNames.prefectureId && prefectures) {
        const matchedPref = prefectures.find(pref => pref.name === result.prefecture_name);
        if (matchedPref) {
          setFieldValue(
            fieldNames.prefectureId,
            matchedPref.id as PathValue<TFieldValues, typeof fieldNames.prefectureId>
          );
        }
      }
    },
    [fieldNames.address, fieldNames.prefectureId, prefectures, setFieldValue, setPostalCodeValue]
  );

  const openAddressModal = useCallback(() => {
    setIsAddressModalOpen(true);
  }, []);

  const closeAddressModal = useCallback(() => {
    setIsAddressModalOpen(false);
  }, []);

  const runAddressSearch = useCallback(
    (
      payload: AddressSearchPayload,
      onSuccess: (results: Town[]) => void,
      onError?: () => void
    ) => {
      addressSearchMutation.mutate(payload, {
        onSuccess,
        onError,
      });
    },
    [addressSearchMutation]
  );

  useEffect(() => {
    if (!fieldNames.prefectureId || !addressValue || !prefectures) return;

    const matchedPref = prefectures.find(pref => addressValue.startsWith(pref.name));
    if (matchedPref) {
      setFieldValue(
        fieldNames.prefectureId,
        matchedPref.id as PathValue<TFieldValues, typeof fieldNames.prefectureId>
      );
    }
  }, [addressValue, fieldNames.prefectureId, prefectures, setFieldValue]);

  const handleAddressSelect = useCallback(
    (result: ModalAddressResult) => {
      if (!result.isFromAddressSearch && result.postal_code1 && result.postal_code2) {
        setPostalCodeValue(`${result.postal_code1}${result.postal_code2}`);
      }
      setFieldValue(
        fieldNames.address,
        result.address as PathValue<TFieldValues, typeof fieldNames.address>
      );

      if (fieldNames.prefectureId && result.prefecture_id) {
        setFieldValue(
          fieldNames.prefectureId,
          result.prefecture_id as PathValue<TFieldValues, typeof fieldNames.prefectureId>
        );
      }
    },
    [fieldNames.address, fieldNames.prefectureId, setFieldValue, setPostalCodeValue]
  );

  const handleAddressButtonClick = useCallback(() => {
    const postalCode = String(postalCodeValue ?? '');
    const normalizedPostalCodeInput = normalizeFullWidthPostalCode(postalCode);
    const cleanPostalCode = normalizedPostalCodeInput.replace(/-/g, '');
    const address = String(addressValue ?? '');

    if (normalizedPostalCodeInput !== postalCode) {
      setPostalCodeValue(normalizedPostalCodeInput);
    }

    if (isReadOnlyMode) {
      openAddressModal();
      return;
    }

    if (cleanPostalCode.length === 7 && /^\d{7}$/.test(cleanPostalCode)) {
      const pc1 = cleanPostalCode.substring(0, 3);
      const pc2 = cleanPostalCode.substring(3, 7);
      runAddressSearch(
        { postal_code1: pc1, postal_code2: pc2 },
        results => {
          if (results && results.length > 0) {
            applyTownResult(results[0]);
          } else {
            openAddressModal();
          }
        },
        openAddressModal
      );
      return;
    }

    if (address) {
      runAddressSearch(
        { address },
        results => {
          if (results && results.length === 1) {
            applyTownResult(results[0]);
          } else {
            openAddressModal();
          }
        },
        openAddressModal
      );
      return;
    }

    openAddressModal();
  }, [
    addressValue,
    applyTownResult,
    isReadOnlyMode,
    openAddressModal,
    postalCodeValue,
    runAddressSearch,
    setPostalCodeValue,
  ]);

  const modalInput = useMemo(() => {
    const normalizedPostalCode = normalizeFullWidthPostalCode(String(postalCodeValue ?? '')).replace(
      /\D/g,
      ''
    );
    const hasAddress = Boolean(String(addressValue ?? '').trim());
    return {
      initialPostalCode1: normalizedPostalCode.substring(0, 3),
      initialPostalCode2: normalizedPostalCode.substring(3, 7),
      initialAddress: String(addressValue ?? ''),
      initialActiveTab: (normalizedPostalCode || hasAddress ? 'search' : 'dropdown') as 'dropdown' | 'search',
    };
  }, [addressValue, postalCodeValue]);

  return {
    isAddressModalOpen,
    closeAddressModal,
    handleAddressButtonClick,
    handleAddressSelect,
    modalInput,
  };
};
